import React from 'react'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { useAPISendPenetapanPegawaiToEkin } from 'api/kinerja/tim-penilai-ps/penetapan'
import { queryKeys } from 'api/kinerja/pengawas-sekolah/config'

import type { TPSSummary } from 'types/kinerja-ps-pegawai'

type TSyncPenetapanToEkinProps = {
  isError?: boolean
  summaryData: TPSSummary
}

export const SyncPenetapanToEkin: React.FC<TSyncPenetapanToEkinProps> = ({
  isError,
  summaryData,
}) => {
  const queryClient = useQueryClient()
  const { period } = usePeriodContext()

  // E-Kinerja Sync
  const isEkinSyncSuccess = summaryData?.penetapan?.status === 'SENT_TO_BKN'
  const isEkinSyncFailed = summaryData?.penetapan?.status === 'ERROR_SEND'
  const isEkinSynced = isEkinSyncSuccess || isEkinSyncFailed

  const { isLoading, mutateAsync: sendToEkin } =
    useAPISendPenetapanPegawaiToEkin({})
  const toast = useToast()

  const submit = async () => {
    try {
      await sendToEkin({ pengawasSekolahIds: [summaryData?.pengawasSekolahId] })
      // Invalidate the Get Summary data
      await queryClient.invalidateQueries([
        queryKeys.API_GET_SUMMARY,
        period.id,
      ])

      toast({
        message: 'Data sedang dalam proses dikirim ke E-Kinerja',
        type: 'success',
      })
    } catch (e) {
      toast({
        message:
          'Data gagal masuk ke dalam proses pengiriman ke E-Kinerja. Coba lagi.',
      })
    }
  }

  return (
    <SimpleCard
      className={clsx(
        'p-4 space-y-2 border w-96',
        isError && '!bg-surface-neutral-default !border-default',
        !isError &&
          !isEkinSynced &&
          '!bg-surface-informational-subdued !border-blue-40',
        isEkinSyncSuccess && '!border-green-50 !bg-surface-success-subdued',
        !isError &&
          isEkinSyncFailed &&
          '!border-red-40 !bg-surface-critical-subdued'
      )}
    >
      <div className="flex space-x-2">
        <div className="max-w-64">
          <Text variant="heading-sm">
            Kirim data Predikat Kinerja ke E-Kinerja
          </Text>
        </div>

        <div className="text-right">
          {isEkinSyncSuccess && (
            <div className="w-24">
              <Icon data-testid="sync-to-ekin-success-icon" color="success">
                check_circle_outline
              </Icon>
            </div>
          )}

          {isEkinSyncFailed && (
            <Button
              data-testid="sync-to-ekin-button"
              disabled={isLoading}
              isLoading={isLoading}
              className="flex-1 !py-2 space-x-1"
              size="sm"
              onClick={submit}
            >
              <Icon color="inverse" fontSize="sm">
                share
              </Icon>
              <span>Kirim</span>
            </Button>
          )}
        </div>
      </div>

      {isError && <Badge>Terjadi kendala sistem. Mohon coba lagi nanti.</Badge>}

      {!isError && (
        <>
          {isEkinSyncSuccess && (
            <Badge variant="success">
              Berhasil dan sudah tampil di E-Kinerja
            </Badge>
          )}

          {isEkinSyncFailed && (
            <>
              <Badge variant="critical">Gagal</Badge>
              {Boolean(summaryData?.penetapan?.ekinSyncErrorMessage) && (
                <Text variant="body-sm">
                  Catatan: {summaryData?.penetapan?.ekinSyncErrorMessage}
                </Text>
              )}
            </>
          )}

          {!isEkinSynced && (
            <>
              <Badge variant="informational">Sedang dikirim</Badge>
            </>
          )}
        </>
      )}
    </SimpleCard>
  )
}
